import { browserIsIE, isArray, isNotEmptyObj } from '@/helpers/helpers'
import { VerificationResponse, ErrorMessage } from '@/helpers/mappers'
import store from '@/store/store'
import Vue from 'vue'
import moment from 'moment'

/**
 * Получить список настроек
 * @return {Promise<Object|null>}
 */
const apiPolicies = () => {
  return Vue.prototype.$request('/policy')
    .then(response => {
      console.log('Настройки приложения получены')
      return isNotEmptyObj(response.data) ? response.data : null
    })
    .catch(error => {
      console.error(error.response?.data || error.message)
      store.dispatch(
        'notification/showError',
        `Ошибка при получении настроек: ${ErrorMessage(error)}`
      )
      return null
    })
}

/**
 * Отправить документ (возвращает id)
 * @param {FormData} document
 * @param {String} docName
 * @return {Promise<String>}
 */
const apiPostDocument = (document, docName) => {
  return Vue.prototype.$request.post('/documents/', document)
    .then(response => response.data?.id ?? '')
    .catch(error => {
      if (error.response?.status === 413) {
        store.dispatch(
          'notification/showError',
          `Не удалось обработать документ ${docName}. Размер файла превышает установленное ограничение на сервере`
        )
        return ''
      }
      store.dispatch(
        'notification/showError',
        `Ошибка при отправке документа ${docName}: ${ErrorMessage(error)}`
      )
      return ''
    })
}

/**
 * Получить список загруженных документов
 * @return {Promise<Object[]|[]>}
 */
const apiPostedDocumentsList = () => {
  return Vue.prototype.$request('/documents/')
    .then(response => {
      if (isArray(response.data)) {
        return response.data
      } else {
        return []
      }
    })
    .catch(error => {
      console.error(error.response?.data || error.message)
      store.dispatch(
        'notification/showError',
        `Ошибка при получении списка документов: ${ErrorMessage(error)}`
      )
      return []
    })
}

/**
 * Получить превью загруженного документа
 * @param {String} id
 * @param {Boolean} blank
 * @return {Promise<Blob|boolean>}
 */
const apiGetPreview = (id, blank = false) => {
  return Vue.prototype.$request({
    method: 'GET',
    url: `/documents/${id}/preview`,
    responseType: 'blob'
  })
    .then((response) => {
      return blank ? true : response.data
    })
    .catch(error => error.response?.data)
}

/**
 * Сбросить/Удалить загруженный документ
 * @param {String} id
 * @return {Promise<Boolean>}
 */
const apiResetDocument = id => {
  return Vue.prototype.$request.post(`/documents/${id}/reset`)
    .then(() => true)
    .catch(error => {
      console.error(error.response?.data || error.message)
      store.dispatch('notification/showError', `Произошла ошибка при удалении: ${ErrorMessage(error)}`)
      return false
    })
}

/**
 * Сбросить/Удалить все документы
 * @return {Promise<Boolean>}
 */
const apiResetAllDocuments = () => {
  return Vue.prototype.$request.post('/documents/reset/all')
    .then(() => true)
    .catch(error => {
      console.error(error.response?.data || error.message)
      store.dispatch('notification/showError', `Произошла ошибка при очистке: ${ErrorMessage(error)}`)
      return false
    })
}

/**
 * Проверить документы
 * @param {Object} params
 * @return {Promise<VerificationResponse>}
 */
const apiVerifyDocuments = params => {
  return Vue.prototype.$request.post('/signatures/package/session', params)
    .then(response => {
      return new VerificationResponse({ successResponse: response })
    })
    .catch(error => {
      return new VerificationResponse({ errorMsg: ErrorMessage(error) })
    })
}

/**
 * Проверить сертификат
 * @param {CertificateRequest} params
 * @return {Promise<VerificationResponse>}
 */
const apiVerifyCertificate = params => {
  return Vue.prototype.$request.post('/certificates', params)
    .then(response => {
      return new VerificationResponse({ successResponse: response })
    })
    .catch(error => {
      let errorMsg = ErrorMessage(error)
      if (error?.response?.data?.status === 500) {
        errorMsg = null
      } else if (error?.response?.status === 413) {
        errorMsg = 'Не удалось обработать некоторые файлы. Размер превышает установленное ограничение на сервере'
      }

      return new VerificationResponse({ errorMsg })
    })
}

/**
 * Получить pdf-отчет проверки
 * @return {Promise<Blob|null>}
 */
const apiGetReport = () => {
  return Vue.prototype.$request({
    method: 'GET',
    url: '/signatures/report/print',
    responseType: 'blob'
  })
    .then(response => {
      if (!response?.data) {
        return null
      } else {
        let filename = response.headers['x-svs-filename'] || `Протокол_${moment().format('DDMMYYYY_Hmmss')}`

        if (browserIsIE()) {
          filename += '.pdf'
        }

        return {
          blob: response.data,
          filename
        }
      }
    })
    .catch(error => {
      const response = error.response
      if (response) {
        console.error(`Код ошибки: ${response.status}, причина: ${response.statusText}`)
      }
      store.dispatch('notification/showError', 'Ошибка! Не удалось скачать файл отчёта')
      return null
    })
}

/**
 * Получить исходный документ
 * @return {Promise<Blob|null>}
 */
const apiGetOriginalDoc = (docId) => {
  return Vue.prototype.$request({
    method: 'GET',
    url: `/signatures/extractcontent/${docId}`,
    responseType: 'blob'
  })
    .then(response => {
      if (!response?.data) {
        return null
      } else {
        const filename = response.headers['x-svs-filename'] || 'unknown.docx'

        try {
          const decodedFilename = decodeURI(filename)
          return {
            blob: response.data,
            filename: decodedFilename
          }
        } catch (e) {
          console.warn('Не удалось декодировать название файла', e)
          return {
            blob: response.data,
            filename
          }
        }
      }
    })
    .catch(error => {
      const response = error.response
      if (response) {
        console.error(`Код ошибки: ${response.status}, причина: ${response.statusText}`)
      }
      store.dispatch('notification/showError', 'Ошибка! Не удалось скачать исходный документ')
      return null
    })
}

/**
 * Получить разметку для справки
 * @return {Promise<string|null>}
 */
const apiGetHelpHTML = () => {
  return Vue.prototype.$request(Vue.prototype.$appConfig.helpURL)
    .then(response => {
      return response.data
    })
    .catch(error => {
      const response = error.response
      if (response) {
        console.error(`Не удалось получить разметку справки. Код ошибки: ${response.status}, причина: ${response.statusText}`)
      }
      return null
    })
}

export {
  apiPolicies,
  apiPostDocument,
  apiGetPreview,
  apiPostedDocumentsList,
  apiResetDocument,
  apiResetAllDocuments,
  apiVerifyDocuments,
  apiVerifyCertificate,
  apiGetReport,
  apiGetOriginalDoc,
  apiGetHelpHTML
}
